import { useState, useRef, useEffect } from 'react';
import Modal from './Modal';

export interface TimelineNode {
  id: string;
  type: 'optimization' | 'manual';
  timestamp: string;
  description: string;
  status: 'running' | 'succeeded' | 'failed';
  starred: boolean;
}

interface TimelineProps {
  nodes: TimelineNode[];
  maxHeight?: number;
  selectedNodeId?: number | null;
  onNodeClick?: (node: TimelineNode) => void;
  onNodeUpdate?: (nodeId: string, updates: Partial<TimelineNode>) => void;
}

export function Timeline({ nodes, maxHeight, selectedNodeId, onNodeClick, onNodeUpdate }: TimelineProps) {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const [editingNode, setEditingNode] = useState<TimelineNode | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [editStarred, setEditStarred] = useState(false);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hideTooltipTimeoutRef = useRef<number | null>(null);

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (!container || !maxHeight) {
      setCanScrollUp(false);
      setCanScrollDown(false);
      return;
    }
    
    setCanScrollUp(container.scrollTop > 5);
    setCanScrollDown(container.scrollTop < container.scrollHeight - container.clientHeight - 5);
    
    // Hide tooltip during scroll to prevent misalignment
    if (hoveredNode && tooltipPosition) {
      if (hideTooltipTimeoutRef.current) {
        clearTimeout(hideTooltipTimeoutRef.current);
        hideTooltipTimeoutRef.current = null;
      }
      setTooltipPosition(null);
      setHoveredNode(null);
    }
  };

  useEffect(() => {
    checkScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, maxHeight]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTooltipTimeoutRef.current) {
        clearTimeout(hideTooltipTimeoutRef.current);
      }
    };
  }, []);

  const handleNodeClick = (node: TimelineNode) => {
    // Don't allow clicking on failed nodes
    if (node.status === 'failed') {
      return;
    }
    if (onNodeClick) {
      onNodeClick(node);
    }
  };

  const handleEditClick = (e: React.MouseEvent, node: TimelineNode) => {
    e.stopPropagation();
    setEditingNode(node);
    setEditDescription(node.description);
    setEditStarred(node.starred);
  };

  const handleSaveEdit = () => {
    if (editingNode && onNodeUpdate) {
      onNodeUpdate(editingNode.id, {
        description: editDescription,
        starred: editStarred,
      });
    }
    setEditingNode(null);
  };

  const handleCancelEdit = () => {
    setEditingNode(null);
  };

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ 
        top: scrollContainerRef.current.scrollHeight, 
        behavior: 'smooth' 
      });
    }
  };

  const scrollableStyle = maxHeight ? {
    maxHeight: `${maxHeight}px`,
    overflowY: 'auto' as const,
    scrollbarWidth: 'none' as const, // Firefox
    msOverflowStyle: 'none' as const, // IE/Edge
  } : {};

  return (
    <>
      <div className="flex flex-col gap-0 items-center">
        <div className="relative flex items-start">
          {/* Main timeline container with padding for hover effects */}
            <div className="relative px-4 w-[96px] py-2 overflow-visible">
              {/* Top fade overlay */}
              {canScrollUp && (
                <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-white via-white to-transparent z-10 pointer-events-none" />
              )}
            
            {/* Scroll Up Indicator */}
            {maxHeight && (
              <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20">
                <div 
                  onClick={scrollToTop}
                  className={`w-6 h-6 rounded-full bg-white border border-black flex items-center justify-center transition-opacity duration-150 ${
                    canScrollUp ? 'opacity-100 cursor-pointer hover:bg-gray-100' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  <svg className="w-3 h-3 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 15l7-7 7 7" />
                  </svg>
                </div>
              </div>
            )}

            <div
              ref={scrollContainerRef}
              style={scrollableStyle}
              onScroll={checkScroll}
              className="relative flex flex-col items-center [&::-webkit-scrollbar]:hidden py-2"
            >
            {nodes.map((node, index) => {
              const isFailed = node.status === 'failed';
              return (
              <div key={node.id} className="relative flex flex-col items-center">
                {/* Failed X indicator above the node */}
                {isFailed && (
                  <div className="flex items-center justify-center mb-1">
                    <svg
                      className="w-4 h-4 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={3}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                )}
                {/* Node Circle */}
                <div
                  className="relative group"
                  onMouseEnter={(e) => {
                    // Clear any pending hide timeout
                    if (hideTooltipTimeoutRef.current) {
                      clearTimeout(hideTooltipTimeoutRef.current);
                      hideTooltipTimeoutRef.current = null;
                    }
                    setHoveredNode(node.id);
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltipPosition({
                      top: rect.top,
                      left: rect.right + 16
                    });
                  }}
                  onMouseLeave={() => {
                    // Delay hiding to allow moving to tooltip
                    hideTooltipTimeoutRef.current = setTimeout(() => {
                      setHoveredNode(null);
                      setTooltipPosition(null);
                    }, 100);
                  }}
                >
                  <div
                    onClick={() => handleNodeClick(node)}
                    className={`
                      w-10 h-10 rounded-full border-3 flex items-center justify-center
                      transition-all duration-200
                      ${isFailed 
                        ? 'bg-red-500 border-red-700 cursor-not-allowed opacity-70' 
                        : `cursor-pointer border-black ${node.type === 'optimization' ? 'bg-black' : 'bg-yellow-100'}`
                      }
                      ${hoveredNode === node.id && !isFailed ? 'scale-110' : ''}
                    `}
                    style={{ 
                      borderWidth: '6px',
                      borderColor: isFailed ? '#b91c1c' : 'black',
                      ...(selectedNodeId === parseInt(node.id, 10) && !isFailed ? {
                        outline: '3px solid #A0635F',
                        outlineOffset: '-3px'
                      } : {})
                    }}
                  >
                    {node.starred && !isFailed && (
                      <svg
                        className={`w-5 h-5 ${node.type === 'optimization' ? 'text-white' : 'text-black'}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    )}
                  </div>
                </div>

                {/* Connecting Line */}
                {index < nodes.length - 1 && (
                  <div className={`w-1 h-2 ${isFailed ? 'bg-red-700' : 'bg-black'}`}></div>
                )}
              </div>
            );})}
            </div>

            {/* Bottom fade overlay */}
            {canScrollDown && (
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white to-transparent z-10 pointer-events-none" />
            )}

            {/* Scroll Down Indicator */}
            {maxHeight && (
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 z-20">
                <div 
                  onClick={scrollToBottom}
                  className={`w-6 h-6 rounded-full bg-white border border-black flex items-center justify-center transition-opacity duration-150 ${
                    canScrollDown ? 'opacity-100 cursor-pointer hover:bg-gray-100' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  <svg className="w-3 h-3 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fixed Position Tooltip */}
      {hoveredNode && tooltipPosition && (
        <div 
          className="fixed bg-black text-white text-xs px-3 py-2 rounded whitespace-nowrap shadow-xl"
          style={{ 
            top: `${tooltipPosition.top}px`, 
            left: `${tooltipPosition.left}px`,
            zIndex: 9999
          }}
          onMouseEnter={() => {
            // Clear hide timeout when hovering over tooltip
            if (hideTooltipTimeoutRef.current) {
              clearTimeout(hideTooltipTimeoutRef.current);
              hideTooltipTimeoutRef.current = null;
            }
          }}
          onMouseLeave={() => {
            // Hide tooltip when leaving it
            setHoveredNode(null);
            setTooltipPosition(null);
          }}
        >
          {(() => {
            const node = nodes.find(n => n.id === hoveredNode);
            if (!node) return null;
            const isFailed = node.status === 'failed';
            return (
              <>
                <div className="font-bold mb-1">ID: {node.id}</div>
                <div className="mb-1">Tipo: {node.type === 'optimization' ? 'Ottimizzazione' : 'Manuale'}</div>
                <div className="mb-1">{node.timestamp}</div>
                {isFailed && (
                  <div className="mb-1 text-red-400 font-semibold">Status: Fallito</div>
                )}
                <div className="max-w-xs mb-2">{node.description}</div>
                {node.starred && !isFailed && (
                  <div className="mb-2 text-yellow-300">★ Preferita</div>
                )}
                {!isFailed && (
                  <button
                    onClick={(e) => handleEditClick(e, node)}
                    className="bg-white text-black px-2 py-1 text-xs font-bold hover:bg-gray-200 transition-colors"
                    style={{ borderRadius: '0px' }}
                  >
                    MODIFICA
                  </button>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Edit Modal */}
      {editingNode && (
        <Modal isOpen={true} onClose={handleCancelEdit}>
          <div className="bg-white p-6 w-full max-w-md min-w-[400px]" style={{ borderRadius: '0px' }}>
            <h2 className="text-xl font-bold mb-4 border-b-2 border-black pb-2">
              Modifica Nodo Timeline
            </h2>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                ID: {editingNode.id}
              </label>
              <div className="text-xs text-gray-500 mb-2">
                Tipo: {editingNode.type === 'optimization' ? 'Ottimizzazione' : 'Manuale'} | {editingNode.timestamp}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Descrizione
              </label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="w-full border-2 border-black px-3 py-2 font-medium"
                style={{ borderRadius: '0px' }}
                rows={4}
              />
            </div>

            <div className="mb-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editStarred}
                  onChange={(e) => setEditStarred(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm font-semibold text-gray-700">
                  Preferita
                </span>
              </label>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 border-2 border-black bg-white text-black font-bold hover:bg-gray-100 transition-colors"
                style={{ borderRadius: '0px' }}
              >
                ANNULLA
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-black text-white font-bold hover:bg-gray-800 transition-colors"
                style={{ borderRadius: '0px' }}
              >
                SALVA
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

export default Timeline;

